import { LocateSkill } from '@sb/ai/types';

export function getInvalidLocateSkillFields(skill: unknown): string[] {
  const result = LocateSkill.safeParse(skill);

  if (result.success) return [];

  return [...new Set(result.error.issues.map((issue) => issue.path.join('.')))];
}

export type MissingLocateSkillConfig = {
  capturePose: boolean;
  // From methodConfig (runtime consumed), not snapshotStorageID (display-only, may be absent).
  teach: boolean;
  referencePlane: boolean;
};

type LocateSkillLike = {
  shared?: Record<string, unknown> | null;
  method?: string | null;
  methodConfig?: Record<string, unknown> | null;
};

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.length > 0;
}

// Methods without taught reference (blob, mock) return false — no false positives.
function isTeachMissing(
  method: string | null | undefined,
  methodConfig: Record<string, unknown> | null | undefined,
): boolean {
  const mc = (methodConfig ?? {}) as Record<string, unknown>;

  switch (method) {
    case 'dino_fast': {
      const { positives } = mc;

      return !(Array.isArray(positives) && positives.length > 0);
    }
    case 'text_prompt':
      return !isNonEmptyString(mc.textInput);
    case 'shape_match': {
      const templateImage = mc.templateImage as
        | { storageID?: unknown }
        | undefined;

      return !(templateImage && isNonEmptyString(templateImage.storageID));
    }
    case 'custom_checkpoint': {
      if (mc.modelSource !== 'roboflow') {
        return !isNonEmptyString(mc.modelPath);
      }

      // A Roboflow source is always a workflow, and needs all three parts: which
      // workflow, which of its outputs feeds locate, and the workspace whose
      // stored key resolves it. None uses a runner-hosted `modelPath`. A skill
      // saved against the retired model path has no workflow id, so it reads as
      // needing teaching rather than being migrated.
      return (
        !isNonEmptyString(mc.roboflowWorkflowId) ||
        !isNonEmptyString(mc.roboflowOutputName) ||
        !isNonEmptyString(mc.roboflowWorkspaceName)
      );
    }
    default:
      return false;
  }
}

export function getMissingLocateSkillConfig(
  skill: LocateSkillLike | null | undefined,
): MissingLocateSkillConfig {
  const shared = (skill?.shared ?? {}) as Record<string, unknown>;

  const capturePose =
    shared.capturePose == null &&
    !isNonEmptyString(shared.capturePoseSpaceItemId);

  const teach = isTeachMissing(skill?.method, skill?.methodConfig);

  const projection = (shared.projection ?? {}) as Record<string, unknown>;

  // Inline plane needs all three axes; other methods use planeSpaceItemId or none.
  const inlinePlane = projection.plane as
    | { origin?: unknown; xAxis?: unknown; yAxis?: unknown }
    | null
    | undefined;

  const inlinePlaneComplete = Boolean(
    inlinePlane?.origin && inlinePlane?.xAxis && inlinePlane?.yAxis,
  );

  let referencePlane: boolean;

  switch (projection.method) {
    case 'Detection3D':
      referencePlane = projection.detection3d == null;
      break;
    // Both depth methods measure at runtime; no preset needed.
    case 'DetectedPlane':
    case 'DetectedDepth':
      referencePlane = false;
      break;
    default:
      referencePlane =
        !inlinePlaneComplete && !isNonEmptyString(projection.planeSpaceItemId);
  }

  return { capturePose, teach, referencePlane };
}
